import { useEffect, useRef, useState } from "react";
import { Reveal } from "@/components/aklive/motion";

const HELP_OPTIONS = [
  "Brand Identity",
  "Website",
  "Pitch Deck / Presentation",
  "Social and Content System",
  "Packaging",
  "Event Branding",
  "Ongoing Design Support",
  "Not Sure Yet",
];
const BUDGETS = ["Under INR 50k", "INR 50k - INR 1L", "INR 1L - INR 3L", "INR 3L+", "Not sure yet"];
const TIMELINES = ["ASAP", "Within 1 month", "1-3 months", "Flexible"];
const API = process.env.REACT_APP_BACKEND_URL;
const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY;

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const inputCls =
  "w-full border-b-2 border-line bg-transparent pb-3 pt-1 text-base text-ink transition-colors duration-300 focus:border-ember focus:outline-none";

const Field = ({ id, label, required, error, children }) => (
  <div>
    <label htmlFor={id} className="mb-2 block text-sm font-medium text-ink">
      {label}
      {required ? <span className="text-ember"> *</span> : <span className="text-ink2"> — optional</span>}
    </label>
    {children}
    {error && (
      <p id={`${id}-error`} role="alert" data-testid={`${id}-error`} className="mt-2 text-sm text-ember">
        {error}
      </p>
    )}
  </div>
);

export const ContactForm = () => {
  const [values, setValues] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    help: [],
    description: "",
    budget: "",
    timeline: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const formRef = useRef(null);
  const captchaRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    const render = () => {
      if (widgetIdRef.current !== null || !captchaRef.current || !window.hcaptcha) return;
      widgetIdRef.current = window.hcaptcha.render(captchaRef.current, {
        sitekey: HCAPTCHA_SITE_KEY,
        callback: (token) => setCaptchaToken(token),
        "expired-callback": () => setCaptchaToken(""),
        "error-callback": () => setCaptchaToken(""),
      });
    };
    if (window.hcaptcha) {
      render();
      return;
    }
    window.aklHcaptchaLoad = render;
    if (!document.querySelector("script[data-hcaptcha]")) {
      const script = document.createElement("script");
      script.src = "https://js.hcaptcha.com/1/api.js?render=explicit&onload=aklHcaptchaLoad";
      script.async = true;
      script.setAttribute("data-hcaptcha", "true");
      document.head.appendChild(script);
    }
  }, []);

  const set = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));
  const toggleHelp = (option) =>
    setValues((v) => ({
      ...v,
      help: v.help.includes(option) ? v.help.filter((h) => h !== option) : [...v.help, option],
    }));

  const validate = () => {
    const er = {};
    if (!values.name.trim()) er.name = "Please enter your name.";
    if (!values.company.trim()) er.company = "Please enter your company or brand.";
    if (!values.email.trim()) er.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) er.email = "Please enter a valid email address.";
    return er;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status !== "idle") return;
    const er = validate();
    if (!captchaToken) er.captcha = "Please complete the verification below.";
    setErrors(er);
    if (Object.keys(er).some((k) => er[k])) {
      const firstId = ["contact-name", "contact-company", "contact-email"].find(
        (id) => er[id.replace("contact-", "")]
      );
      if (firstId) formRef.current?.querySelector(`#${firstId}`)?.focus();
      return;
    }
    setStatus("submitting");
    setServerError("");
    try {
      const res = await fetch(`${API}/api/contact/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: values.name,
          company: values.company,
          email: values.email,
          phone: values.phone,
          help: values.help,
          description: values.description,
          budget: values.budget,
          timeline: values.timeline,
          website: honeypot,
          hcaptcha_token: captchaToken,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const detail = typeof data.detail === "string" ? data.detail : "Something went wrong. Please try again.";
        throw new Error(detail);
      }
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setServerError(err.message || "Something went wrong. Please try again.");
      if (window.hcaptcha && widgetIdRef.current !== null) {
        window.hcaptcha.reset(widgetIdRef.current);
        setCaptchaToken("");
      }
    }
  };

  if (status === "success") {
    return (
      <div role="status" aria-live="polite" data-testid="contact-success" className="border-t border-line pt-8">
        <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-ink md:text-4xl">
          Thanks. We've received your project details<span className="text-ember">.</span>
        </h2>
        <ol className="mt-10 space-y-6">
          {["We review the project.", "We determine the right scope.", "We get back to you to discuss next steps."].map(
            (step, i) => (
              <li key={step} className="flex items-baseline gap-5 border-b border-line pb-6">
                <span className="font-mono text-xs tracking-[0.2em] text-ember">{i + 1}</span>
                <span className="font-display text-lg font-bold tracking-tight text-ink md:text-xl">{step}</span>
              </li>
            )
          )}
        </ol>
      </div>
    );
  }

  return (
    <Reveal>
      <form ref={formRef} onSubmit={onSubmit} noValidate data-testid="contact-form" className="relative space-y-12">
        <div aria-hidden="true" className="absolute left-[-9999px] top-0 h-px w-px overflow-hidden">
          <label htmlFor="contact-website-hp">Website</label>
          <input
            id="contact-website-hp"
            data-testid="contact-honeypot-input"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </div>
        <p className="sr-only" aria-live="polite">
          {Object.keys(errors).filter((k) => errors[k]).length > 0
            ? "The form has errors. Please review the highlighted fields."
            : ""}
        </p>

        <div className="grid gap-8 border-t border-line pt-8 md:grid-cols-2 md:gap-x-10">
          <Field id="contact-name" label="Name" required error={errors.name}>
            <input
              id="contact-name"
              data-testid="contact-name-input"
              type="text"
              autoComplete="name"
              value={values.name}
              onChange={set("name")}
              aria-required="true"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? "contact-name-error" : undefined}
              className={inputCls}
            />
          </Field>
          <Field id="contact-company" label="Company / Brand" required error={errors.company}>
            <input
              id="contact-company"
              data-testid="contact-company-input"
              type="text"
              autoComplete="organization"
              value={values.company}
              onChange={set("company")}
              aria-required="true"
              aria-invalid={!!errors.company}
              aria-describedby={errors.company ? "contact-company-error" : undefined}
              className={inputCls}
            />
          </Field>
          <Field id="contact-email" label="Email" required error={errors.email}>
            <input
              id="contact-email"
              data-testid="contact-email-input"
              type="email"
              autoComplete="email"
              value={values.email}
              onChange={set("email")}
              aria-required="true"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "contact-email-error" : undefined}
              className={inputCls}
            />
          </Field>
          <Field id="contact-phone" label="Phone or WhatsApp" error={errors.phone}>
            <input
              id="contact-phone"
              data-testid="contact-phone-input"
              type="tel"
              autoComplete="tel"
              value={values.phone}
              onChange={set("phone")}
              className={inputCls}
            />
          </Field>
        </div>

        <div className="border-t border-line pt-8" role="group" aria-labelledby="help-label">
          <p id="help-label" className="mb-5 text-sm font-medium text-ink">
            What do you need help with?<span className="text-ink2"> — select any</span>
          </p>
          <div className="grid gap-1 sm:grid-cols-2">
            {HELP_OPTIONS.map((option) => (
              <label
                key={option}
                className="flex min-h-[44px] cursor-pointer items-center gap-3 text-base text-ink"
              >
                <input
                  type="checkbox"
                  data-testid={`help-checkbox-${slugify(option)}`}
                  checked={values.help.includes(option)}
                  onChange={() => toggleHelp(option)}
                  className="h-5 w-5 flex-shrink-0 accent-ember"
                />
                {option}
              </label>
            ))}
          </div>
        </div>

        <div className="border-t border-line pt-8">
          <Field id="contact-description" label="Project description" error={errors.description}>
            <p id="contact-description-hint" className="mb-3 text-sm text-ink2">
              What are you trying to build, launch or improve?
            </p>
            <textarea
              id="contact-description"
              data-testid="contact-description-input"
              rows={5}
              value={values.description}
              onChange={set("description")}
              aria-describedby="contact-description-hint"
              className="w-full border border-line bg-transparent p-4 text-base text-ink transition-colors duration-300 focus:border-ember focus:outline-none"
            />
          </Field>
        </div>

        <div className="border-t border-line pt-8" role="radiogroup" aria-labelledby="budget-label">
          <p id="budget-label" className="mb-5 text-sm font-medium text-ink">Budget</p>
          <div className="grid gap-1 sm:grid-cols-2">
            {BUDGETS.map((option, i) => (
              <label key={option} className="flex min-h-[44px] cursor-pointer items-center gap-3 text-base text-ink">
                <input
                  type="radio"
                  name="budget"
                  data-testid={`budget-radio-${i}`}
                  checked={values.budget === option}
                  onChange={() => setValues((v) => ({ ...v, budget: option }))}
                  className="h-5 w-5 flex-shrink-0 accent-ember"
                />
                {option}
              </label>
            ))}
          </div>
        </div>

        <div className="border-t border-line pt-8" role="radiogroup" aria-labelledby="timeline-label">
          <p id="timeline-label" className="mb-5 text-sm font-medium text-ink">Timeline</p>
          <div className="grid gap-1 sm:grid-cols-2">
            {TIMELINES.map((option, i) => (
              <label key={option} className="flex min-h-[44px] cursor-pointer items-center gap-3 text-base text-ink">
                <input
                  type="radio"
                  name="timeline"
                  data-testid={`timeline-radio-${i}`}
                  checked={values.timeline === option}
                  onChange={() => setValues((v) => ({ ...v, timeline: option }))}
                  className="h-5 w-5 flex-shrink-0 accent-ember"
                />
                {option}
              </label>
            ))}
          </div>
        </div>

        <div className="border-t border-line pt-8">
          <p className="text-sm font-medium text-ink">Have a brief, deck or reference?</p>
          <p className="mt-2 text-sm leading-relaxed text-ink2" data-testid="contact-upload-coming-soon">
            File upload is coming soon. For now, email it to{" "}
            <a href="mailto:connect@aklive.studio" className="link-underline font-medium text-ink hover:text-ember">
              connect@aklive.studio
            </a>
            .
          </p>
        </div>

        <div className="border-t border-line pt-8">
          <p className="mb-4 text-sm font-medium text-ink">Verification</p>
          <div ref={captchaRef} data-testid="contact-hcaptcha" />
          {errors.captcha && (
            <p role="alert" data-testid="contact-captcha-error" className="mt-2 text-sm text-ember">
              {errors.captcha}
            </p>
          )}
        </div>

        <div className="border-t border-line pt-8">
          {serverError && (
            <div role="alert" data-testid="contact-server-error" className="mb-6 border border-line bg-paper2 p-4">
              <p className="text-sm font-medium text-ember">{serverError}</p>
              <p className="mt-1 text-sm text-ink2">
                Prefer email?{" "}
                <a href="mailto:connect@aklive.studio" className="link-underline font-medium text-ink hover:text-ember">
                  connect@aklive.studio
                </a>
              </p>
            </div>
          )}
          <button
            type="submit"
            data-testid="contact-submit-btn"
            disabled={status !== "idle"}
            aria-busy={status === "submitting"}
            className="btn-primary h-14 w-full px-8 text-base disabled:opacity-60 sm:w-auto"
          >
            {status === "submitting" ? "Sending…" : "Send Project Details"}
          </button>
        </div>
      </form>
    </Reveal>
  );
};
